import { z } from 'zod';
import type { PageOrientation } from '@record-fill';
import type { FormElement, ImageProperties, TableCell, TableProperties } from '../model/form.types';
import { migrateNumberInputProperties } from './migrateNumberInputProperties';

function sanitizeHeaderLogoImageUrl(elements: FormElement[]): FormElement[] {
  return elements.map((element) => {
    if (element.type !== 'TABLE') {
      return element;
    }

    const tableProperties = element.properties as TableProperties;
    if (tableProperties.isHeader !== true) {
      return element;
    }

    const cells = tableProperties.cells.map((row: TableCell[], rowIndex: number) =>
      row.map((cell: TableCell, colIndex: number) => {
        const content = cell.content;
        if (!content || content.type !== 'IMAGE') {
          return cell;
        }

        const imageName = content.properties.name ?? '';
        const isHeaderLogoCell = rowIndex === 0 && colIndex === 0;
        const isHeaderLogoByName = imageName.startsWith('logo_');

        if (!isHeaderLogoCell && !isHeaderLogoByName) {
          return cell;
        }

        const imageProperties = content.properties as ImageProperties;
        const { imageUrl, ...restProperties } = imageProperties;
        return {
          ...cell,
          content: {
            ...content,
            properties: restProperties,
          },
        };
      })
    );

    return {
      ...element,
      properties: {
        ...tableProperties,
        cells,
      },
    };
  });
}

export interface FormMetadata {
  prefix: string;
  title?: string;
  orientation?: PageOrientation;
  createdAt: string;
  updatedAt: string;
}

export interface FormDefinition {
  version: '1.0';
  metadata: FormMetadata;
  elements: FormElement[];
}

const SpacingSchema = z.object({
  top: z.number(),
  right: z.number(),
  bottom: z.number(),
  left: z.number(),
});

const BorderStyleSchema = z.object({
  width: z.number().optional(),
  style: z.enum(['solid', 'dashed', 'dotted']).optional(),
  color: z.string().optional(),
});

const CellBorderSchema = z.object({
  top: BorderStyleSchema.optional(),
  right: BorderStyleSchema.optional(),
  bottom: BorderStyleSchema.optional(),
  left: BorderStyleSchema.optional(),
});

const CellPropertiesSchema = z.object({
  backgroundColor: z.string().optional(),
  padding: z.number().optional(),
  alignment: z.enum(['left', 'center', 'right']).optional(),
  border: CellBorderSchema.optional(),
});

const TableCellSchema: z.ZodType<unknown> = z.lazy(() =>
  z.object({
    rowIndex: z.number(),
    colIndex: z.number(),
    rowSpan: z.number().default(1),
    colSpan: z.number().default(1),
    isMerged: z.boolean().optional(),
    mergeGroupId: z.string().optional(),
    properties: CellPropertiesSchema.optional(),
    content: FormElementSchema.optional(),
  })
);

const BaseElementPropertiesSchema = z.object({
  name: z.string(),
  label: z.string().optional(),
  content: z.string().optional(),
  placeholder: z.string().optional(),
  fontSize: z.number().optional(),
  fontWeight: z.enum(['normal', 'semibold', 'bold']).optional(),
  textAlign: z.enum(['left', 'center', 'right']).optional(),
  color: z.string().optional(),
  backgroundColor: z.string().optional(),
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  regex: z.string().optional(),
  required: z.boolean().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().optional(),
  minValue: z.union([z.string(), z.number()]).optional(),
  maxValue: z.union([z.string(), z.number()]).optional(),
  numberType: z.enum(['integer', 'decimal']).optional(),
  decimalPlaces: z.number().int().min(0).optional(),
  decimals: z.number().optional(),
  unsigned: z.boolean().optional(),
  rows: z.number().optional(),
  columns: z.number().optional(),
  allowAddRows: z.boolean().optional(),
  allowAddColumns: z.boolean().optional(),
  is_multiple: z.boolean().optional(),
  cells: z.array(z.array(TableCellSchema)).optional(),
  borderWidth: z.number().optional(),
  borderColor: z.string().optional(),
  borderStyle: z.enum(['solid', 'dashed', 'dotted']).optional(),
  borderPattern: z
    .enum(['all', 'top', 'right', 'bottom', 'left', 'top-bottom', 'left-right', 'none'])
    .optional(),
  tableWidth: z.number().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  imageUrl: z.string().optional(),
  allowChange: z.boolean().optional(),
  editable: z.boolean().optional(),
  value: z.string().optional(),
  margin: SpacingSchema.optional(),
  marginByPage: z.record(z.number(), SpacingSchema).optional(),
  mainLabel: z.string().optional(),
  rq: z.string().optional(),
  rev: z.string().optional(),
  title: z.string().optional(),
  isHeader: z.boolean().optional(),
});

const FormElementSchema: z.ZodType<unknown> = z.lazy(() =>
  z.object({
    id: z.string(),
    type: z.enum(['HEADER', 'FIXED_TEXT', 'TEXT_INPUT', 'PARAGRAPH', 'NUMBER_INPUT', 'TABLE', 'IMAGE']),
    position: z.number(),
    properties: BaseElementPropertiesSchema,
  })
);

const FormDefinitionSchema = z.object({
  version: z.literal('1.0'),
  metadata: z.object({
    prefix: z.string().min(1, 'Prefixo é obrigatório'),
    title: z.string().optional(),
    orientation: z.enum(['portrait', 'landscape']).optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
  elements: z.array(FormElementSchema),
});

export function serializeForm(
  elements: FormElement[],
  metadata: Omit<FormMetadata, 'createdAt' | 'updatedAt'>,
  existingMetadata?: Pick<FormMetadata, 'createdAt'>
): string {
  const now = new Date().toISOString();
  const sanitizedElements = sanitizeHeaderLogoImageUrl(elements);

  const formDefinition: FormDefinition = {
    version: '1.0',
    metadata: {
      ...metadata,
      createdAt: existingMetadata?.createdAt || now,
      updatedAt: now,
    },
    elements: JSON.parse(JSON.stringify(sanitizedElements)),
  };

  return JSON.stringify(formDefinition, null, 2);
}

export interface DeserializationResult {
  success: boolean;
  data?: {
    metadata: FormMetadata;
    elements: FormElement[];
  };
  errors?: string[];
}

export function deserializeForm(json: string): DeserializationResult {
  try {
    const parsed = JSON.parse(json);

    const validationResult = FormDefinitionSchema.safeParse(parsed);

    if (!validationResult.success) {
      const errors = validationResult.error.issues.map((err) => {
        const path = err.path.map(String).join('.');
        return `${path}: ${err.message}`;
      });
      return {
        success: false,
        errors,
      };
    }

    const formDefinition = validationResult.data;
    const elements = migrateNumberInputProperties(formDefinition.elements as FormElement[]);

    const elementIds = new Set<string>();
    for (const element of elements) {
      if (elementIds.has(element.id)) {
        return {
          success: false,
          errors: [`ID duplicado encontrado: ${element.id}`],
        };
      }
      elementIds.add(element.id);
    }

    return {
      success: true,
      data: {
        metadata: formDefinition.metadata,
        elements,
      },
    };
  } catch (error) {
    return {
      success: false,
      errors: [error instanceof Error ? error.message : 'Erro ao processar JSON'],
    };
  }
}
